<template>
  <div id="delivery-screen">
    <div class="help-icon">
      <v-popover offset="5">
        <div class="img-wrapper">
          <img height="20" src="/static/icons/help-icon.svg" />
        </div>
        <template
          slot="popover"
          autoHide
          placement="bottom"
          container="img-wrapper"
        >
          <div
            class="welcome-text"
            v-html="$t('deliveryScreen.partnerPicker.welcomeText')"
          ></div>
        </template>
      </v-popover>
    </div>
    <div class="component-wrapper">
      <transition name="fade" mode="out-in">
        <component
          :is="currentComponent.component.is"
          v-bind="currentComponent.component"
          v-on="currentComponent.component.on"
          @getCoords="saveCoords"
          @coordsError="saveCoordsError"
        ></component>
      </transition>
    </div>
    <div class="footer-wrapper">
      <div class="footer">
        <div class="back-button">
          <rounded-button
            height="30px"
            position="left"
            :isOutline="true"
            @click="showBackPage"
            v-if="currentScreenIndex !== 0"
          >
            {{ $t("deliveryScreen.footer.back") }}
          </rounded-button>
        </div>
        <div class="next-button">
          <rounded-button
            height="30px"
            position="right"
            @click="showNextPage"
            :disabled="calculateButtonType"
            :loading="isLoading"
            v-if="currentScreenIndex !== 2"
          >
            {{ nextButtonText }}
          </rounded-button>
          <rounded-button
            height="30px"
            position="right"
            :isOutline="false"
            @click="saveMoving"
            :disabled="saveButtonType"
            v-if="currentScreenIndex === 2"
          >
            {{ $t("deliveryScreen.footer.save") }}
          </rounded-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BoxReader from "@/components/shared/BoxReader";
import PartnerPicker from "@/components/modules/Delivery/PartnerPicker";
import SummaryScreen from "@/components/modules/Delivery/SummaryScreen";
import ConfirmationModal from "@/components/shared/Modals/ConfirmationModal";
import { mapActions, mapGetters } from "vuex";
export default {
  name: "DeliveryScreen",
  props: {},
  components: {},
  data: () => ({
    movedFromPartnerScreen: false,
    currentScreenIndex: 0,
    buttonLoading: false,
    userCoordinates: undefined
  }),
  created() {
    this.currentScreenIndex = this.initScreen();
  },
  watch: {
    getQR() {
      if (this.getQR.length === 0) {
        this.movedFromPartnerScreen = false;
      } else {
        this.movedFromPartnerScreen = true;
      }
    }
  },
  computed: {
    ...mapGetters({
      getPartner: "moving/getPartner",
      getQR: "moving/getQR",
      getWithKeyBoard: "moving/getWithKeyBoard"
    }),
    nextButtonText() {
      if (this.currentScreenIndex == 0) {
        return this.$t("deliveryScreen.footer.nextToQR");
      }
      if (this.currentScreenIndex == 1) {
        return this.$t("deliveryScreen.footer.nextToSummary");
      }
      return this.$t("deliveryScreen.footer.next");
    },
    detectMobile() {
      const toMatch = [
        /Android/i,
        /webOS/i,
        /iPhone/i,
        /iPad/i,
        /iPod/i,
        /BlackBerry/i,
        /Windows Phone/i
      ];

      return toMatch.some(toMatchItem => {
        return navigator.userAgent.match(toMatchItem);
      });
    },
    computedData() {
      const data = {};
      if (this.getQR.length > 0) {
        data["boxQRs"] = this.getQR;
        data["withKeyBoardQRs"] = this.getWithKeyBoard;
      }
      if ("moved" in this.getPartner && "partner" in this.getPartner) {
        data["moved"] = this.getPartner.moved;
        data["partnerID"] = this.getPartner.partner.id;
        data["isMobile"] = this.detectMobile;
      }
      if (this.userCoordinates) {
        if (this.userCoordinates.hasError) {
          data["exactLocation"] = JSON.stringify({
            message: this.userCoordinates.message,
            code: this.userCoordinates.code
          });
        } else {
          data["exactLocation"] = JSON.stringify({
            lat: this.userCoordinates.latitude,
            long: this.userCoordinates.longitude
          });
        }
      } else {
        data["exactLocation"] = null;
      }
      return data;
    },
    saveButtonType() {
      return !(
        "moved" in this.computedData &&
        "partnerID" in this.computedData &&
        "boxQRs" in this.computedData
      );
    },
    isLoading() {
      return this.buttonLoading;
    },
    calculateButtonType() {
      if (this.currentScreenIndex === 0) {
        const obj = this.getPartner;
        return !("moved" in obj) && !("partner" in obj);
      } else if (this.currentScreenIndex === 1) {
        return this.getQR.length === 0;
      }
      return false;
    },
    currentComponent() {
      return this.screens[this.currentScreenIndex];
    },
    screens() {
      return [
        {
          component: { is: PartnerPicker }
        },
        {
          component: { is: BoxReader }
        },
        {
          component: { is: SummaryScreen }
        }
      ];
    }
  },
  methods: {
    ...mapActions({
      createMovement: "movements/createMovement",
      removeMoving: "moving/removeMoving"
    }),
    saveCoordsError(err) {
      this.userCoordinates = {
        code: err.code,
        message: err.message,
        hasError: true
      };
    },
    saveCoords(e) {
      this.userCoordinates = e;
    },
    getUserLocation() {
      return new Promise(resolve => {
        if ("geolocation" in navigator) {
          navigator.geolocation.getCurrentPosition(
            pos => {
              if (pos && pos.coords) {
                resolve(
                  JSON.stringify({
                    lat: pos.coords.latitude,
                    long: pos.coords.longitude
                  })
                );
              } else {
                resolve(undefined);
              }
            },
            err => {
              resolve(
                JSON.stringify({
                  message: err.message,
                  code: err.code
                })
              );
            },
            { timeout: 10000 }
          );
        } else {
          resolve(undefined);
        }
      });
    },
    async saveMoving() {
      const data = this.computedData;
      if (data.exactLocation == null) {
        data.exactLocation = await this.getUserLocation();
      }
      this.createMovement(data)
        .then(res => {
          if (res.status === "OK") {
            this.removeMoving();
            this.currentScreenIndex = 0;
            this.$notification.showNoti(
              "success",
              "deliveryScreen.saveMovement.success"
            );
          } else {
            this.$notification.showNoti("error", null, res);
          }
        })
        .catch(() => {
          this.$notification.showNoti(
            "error",
            "deliveryScreen.saveMovement.error"
          );
        });
    },
    initScreen() {
      const partnerObj = this.getPartner;
      const qrObj = this.getQR;
      if (!("moved" in partnerObj) && !("partner" in partnerObj)) {
        return 0;
      } else if (qrObj.length === 0) {
        return 1;
      } else {
        this.movedFromPartnerScreen = true;
        return 2;
      }
    },
    showBackPage() {
      if (this.movedFromPartnerScreen && this.currentScreenIndex === 1) {
        this.$eventBus.showModal({
          title: this.$t(
            "deliveryScreen.goToPartnerScreen.confirmationModal.title"
          ),
          bind: {
            is: ConfirmationModal,
            width: "500px",
            text: this.$t(
              "deliveryScreen.goToPartnerScreen.confirmationModal.text"
            )
          },
          on: {
            okClicked: () => {
              this.currentScreenIndex -= 1;
            }
          }
        });
      } else {
        this.currentScreenIndex -= 1;
      }
    },
    showNextPage() {
      if (
        this.currentScreenIndex === 0 &&
        this.getPartner.partner.distance > 0.2
      ) {
        this.$eventBus.showModal({
          title: this.$t("deliveryScreen.distanceConfirmation.title"),
          bind: {
            is: ConfirmationModal,
            width: "500px",
            text: this.$t("deliveryScreen.distanceConfirmation.text", {
              partner: this.getPartner.partner.name
            })
          },
          on: {
            okClicked: () => {
              this.$eventBus.closeModal();
              this.currentScreenIndex += 1;
            }
          }
        });
      } else {
        this.currentScreenIndex += 1;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
#delivery-screen {
  .help-icon {
    cursor: pointer;
    position: absolute;
    top: -20px;
    right: 5px;
  }
  .footer-wrapper {
    width: 100%;
    height: 30px;
    padding-top: 20px;
    padding-bottom: 20px;
    margin: auto;
    max-width: 500px;
    .footer {
      max-width: 500px;
      position: fixed;
      bottom: 10px;
      width: calc(100% - 20px);
      display: flex;
      /deep/ #rounded-button-wrapper {
        white-space: nowrap;
        min-width: 130px;
        max-width: 200px;
      }
      .back-button {
        padding-right: 10px;
        width: 50%;
      }
      .next-button {
        padding-left: 10px;
        width: 50%;
      }
    }
  }
}
</style>
